import {Injectable} from '@angular/core';

import {
	HttpStatusCodesEnum,
	FundOrdersService,
	FundSummaryReportInterface,
	FundOpenPositionsReportInterface,
	FundPortfolioPerformanceReportInterface,
	AccountBalanceResponseInterface,
	FundInvestmentReportInterface,
	FundInvestmentRowInterface, AccountSubTypeEnum,
} from '@spa/api';

import {Observable, ReplaySubject} from 'rxjs';
import {AlertsService, Client, ClientsService} from 'src/app/shared/services';
import {AlertTypesEnum} from 'src/app/shared/enums';
import {delay, map} from "rxjs/operators";
import {TradingClientService} from "./trading-client.service";

@Injectable()
export class InvestmentsService {
	private readonly client: Client;
	public investment: FundInvestmentRowInterface;
	public investmentLoading: boolean;
	public cashInvestment: AccountBalanceResponseInterface;
	public cashInvestmentLoading: boolean;
	public currentAccountBalance: ReplaySubject<AccountBalanceResponseInterface>;
	public accountSummaryReport: ReplaySubject<FundSummaryReportInterface>;
	public portfolioPerformanceReport: ReplaySubject<FundPortfolioPerformanceReportInterface[]>;
	public openPositionsReport: ReplaySubject<FundOpenPositionsReportInterface>;

	constructor(
		private readonly alertsService: AlertsService,
		private readonly tradingClientService: TradingClientService,
		private readonly fundOrdersService: FundOrdersService,
		private readonly clientsService: ClientsService
	) {
		this.currentAccountBalance = new ReplaySubject();
		this.portfolioPerformanceReport = new ReplaySubject();
		this.openPositionsReport = new ReplaySubject();
		this.accountSummaryReport = new ReplaySubject();
		this.cashInvestmentLoading = false;
		this.investmentLoading = true;

		this.client = this.clientsService.getCurrent()!;

		this.refreshAccountBalance(false);
		this.refreshCurrentInvestmentsReport();
		this.refreshAccountSummaryReport();
		this.refreshOpenPositionsReport();
		this.refreshPortfolioPerformanceReport();
	}

	public refreshOpenPositionsReport(): void {
		if (!this.client) {
			return;
		}

		this.fundOrdersService
			.getFundOpenPositions(this.client.customerId)
			.subscribe(response => {
				if (response.status !== HttpStatusCodesEnum.Ok) {
					if (response.status !== HttpStatusCodesEnum.NotFound) {
						this.alertsService.addAlert('Failed to retrieve account summary.', AlertTypesEnum.Error);
					}

					return;
				}

				const report = response.data as FundOpenPositionsReportInterface;

				this.openPositionsReport.next(report);
			});
	}

	private refreshAccountSummaryReport(): void {
		if (!this.client) {
			return;
		}

		this.fundOrdersService
			.getFundSummary(this.client.customerId)
			.subscribe(response => {
				if (response.status !== HttpStatusCodesEnum.Ok) {
					if (response.status !== HttpStatusCodesEnum.NotFound) {
						this.alertsService.addAlert('Failed to retrieve account summary.', AlertTypesEnum.Error);
					}

					return;
				}

				const report = response.data as FundSummaryReportInterface;

				this.accountSummaryReport.next(report);
			});
	}

	private refreshPortfolioPerformanceReport(): void {
		if (!this.client) {
			return;
		}

		this.fundOrdersService
			.getFundPortfolioPerformance(this.client.customerId)
			.subscribe(response => {
				if (response.status !== HttpStatusCodesEnum.Ok) {
					if (response.status !== HttpStatusCodesEnum.NotFound) {
						this.alertsService.addAlert('Failed to retrieve portfolio performance.', AlertTypesEnum.Error);
					}

					return;
				}

				const report = response.data as FundPortfolioPerformanceReportInterface[];

				this.portfolioPerformanceReport.next(report);
			});
	}

	public refreshAccountBalance(backoffRetry: boolean, showLoading = true): void {
		if (!this.client) {
			return;
		}

		const tradingAccount = this.tradingClientService.getTradingAccountForCash();

		if (!tradingAccount) {
			return;
		}

		if (showLoading) {
			this.cashInvestmentLoading = true;
		}

		const fetchFn = this.getCurrentCashBalance(tradingAccount.referenceId);

		if (backoffRetry) {
			fetchFn.pipe(
				delay(100),
				map(r => this.handleAccountBalance(r)))
				.subscribe(result => {
					if (result === false) {
						fetchFn.pipe(
							delay(1500),
							map(r => this.handleAccountBalance(r)))
							.subscribe(result => {
								if (result === false) {
									fetchFn.pipe(
										delay(8000),
										map(r => this.handleAccountBalance(r)))
										.subscribe(result => {
											if (result === false) {
												fetchFn.pipe(
													delay(20000),
													map(r => this.handleAccountBalance(r)))
													.subscribe(result => {
														this.cashInvestmentLoading = false;
													})
											}
										})
								}
							})
					}
				});
		} else {
			fetchFn.subscribe(r => this.handleAccountBalance(r));
		}
	}

	private getCurrentCashBalance(accountReferenceId: string): Observable<AccountBalanceResponseInterface> {
		return this.fundOrdersService
			.getAccountBalance({accountReferenceId})
			.pipe(map(response => {
				if (response.status !== HttpStatusCodesEnum.Ok) {
					this.alertsService.addAlert('Failed to retrieve cash balance.', AlertTypesEnum.Error);

					return null;
				}

				const report = response.data as AccountBalanceResponseInterface;

				this.currentAccountBalance.next(report);
				return report;
			}));
	}

	private refreshCurrentInvestmentsReport(): void {
		this.fundOrdersService
			.getCurrentCashInvestments(this.client.customerId)
			.subscribe(response => {
				this.investmentLoading = false;

				if (response.status !== HttpStatusCodesEnum.Ok) {
					this.alertsService.addAlert('Failed to retrieve investments.', AlertTypesEnum.Error);

					return null;
				}

				const report = response.data as FundInvestmentReportInterface;

				this.handleInvestmentReport(report);
			});
	}

	private handleInvestmentReport(report: FundInvestmentReportInterface): boolean | null {
		if (!report || !report.rows) {
			this.cashInvestmentLoading = false;
			return null;
		}

		const investment = report.rows.find(row => !row.isCashRow);

		if (!investment) {
			this.cashInvestmentLoading = false;
			return null;
		}

		if (!this.investment || this.investment.amount !== investment.amount || this.investment.isExtractedFromSnapshot !== investment.isExtractedFromSnapshot) {
			this.investment = investment;

			this.cashInvestmentLoading = false;
			return true
		}

		return false;
	}

	private handleAccountBalance(response: AccountBalanceResponseInterface): boolean | null {
		if (!response) {
			this.cashInvestmentLoading = false;
			return null;
		}

		if (!this.cashInvestment || this.cashInvestment.balance !== response.balance || this.cashInvestment.isExtractedFromSnapshot !== response.isExtractedFromSnapshot) {
			this.cashInvestment = response;
			// this.investment = investment;

			this.cashInvestmentLoading = false;
			return true
		}

		return false;
	}
}
